import { v7 as uuidv7 } from 'uuid';
import { getDexieStore } from './dexie.js';
import { assignmentOf, enqueue } from './outbox.js';
import type { OutboxEntry, SyncStore } from './store.js';

// Offline penalty entry for the referee apps (PWA + native). Penalties are NOT
// cached in their own table (no IndexedDB/SQLite schema change): the screen
// shows the server's penalties (sessions.liveAssignments `penalties` or
// penalties.listByAssignment) merged with the queued outbox entries via
// `mergePenaltyView`. Points always come from the server (the stage's scoring
// schema); offline previews use liveAssignments `penaltyValues`.

/** Mirrors the DB enum `penalty_code` (kept local: @flm9/sync has no scoring dep). */
export type PenaltyCode = 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';

/** Payload of an `penalty.add` outbox entry = input of tRPC `penalties.add`. */
export interface PenaltyAddPayload {
  roundAssignmentId: string;
  code: PenaltyCode;
  clientIdempotencyKey: string;
  note?: string;
}

/** Payload of a `penalty.remove` outbox entry = input of tRPC `penalties.remove`. */
export interface PenaltyRemovePayload {
  roundAssignmentId: string;
  clientIdempotencyKey: string;
}

/** One penalty as the server reports it (liveAssignments / listByAssignment). */
export interface ServerPenaltyRow {
  id: string;
  code: PenaltyCode;
  points: number;
  /** Null for penalties recorded before referee penalty entry (no offline key). */
  clientIdempotencyKey: string | null;
  note?: string | null;
}

export interface PenaltyViewRow {
  /** Stable list key: clientIdempotencyKey, else the server id. */
  key: string;
  /** Server id; null while the add is still queued. */
  id: string | null;
  clientIdempotencyKey: string | null;
  code: PenaltyCode;
  /** Server points; for a queued add the schema value from `values`, else null. */
  points: number | null;
  note: string | null;
  /**
   * synced = on the server; pending = add/remove queued; failed = the server
   * rejected the queued add/remove (lastError has the German reason).
   */
  status: 'synced' | 'pending' | 'failed';
  lastError?: string;
}

/**
 * Queue a penalty (optimistic): enqueues `penalty.add` with a fresh
 * idempotency key. The caller wakes the drainer.
 */
export async function queuePenalty(
  input: { assignmentId: string; code: PenaltyCode; note?: string },
  store: SyncStore = getDexieStore(),
): Promise<PenaltyAddPayload> {
  const payload: PenaltyAddPayload = {
    roundAssignmentId: input.assignmentId,
    code: input.code,
    clientIdempotencyKey: uuidv7(),
    ...(input.note ? { note: input.note } : {}),
  };
  await enqueue('penalty.add', payload, store);
  return payload;
}

/**
 * Remove a penalty by its idempotency key. A queued add that was never
 * submitted is simply dropped (acked); otherwise a `penalty.remove` is queued
 * behind it, so the causal order add → remove is kept even if the add is
 * still in flight. A rejected (failed_terminal) add is dropped and the remove
 * queued anyway. Penalties without a key (legacy rows) cannot be removed
 * offline — call `penalties.remove({ roundAssignmentId, id })` online.
 */
export async function removeQueuedPenalty(
  assignmentId: string,
  clientIdempotencyKey: string,
  store: SyncStore = getDexieStore(),
): Promise<void> {
  await store.transaction(async (tx) => {
    const add = (await tx.listPendingAndInflight()).find(
      (e) => e.op === 'penalty.add' && matches(e, assignmentId, clientIdempotencyKey),
    );
    // Same "never submitted" rule as correctQueuedShot: a paused attempt gives
    // its attempt back but keeps lastError — the server may have saved it.
    if (add?.status === 'pending' && add.attempts === 0 && add.lastError == null) {
      await tx.updateOutbox(add.id, { status: 'acked' });
      return;
    }
    // A rejected add would keep blocking the assignment's queue: drop it, and
    // still send the (idempotent) remove in case it did land after all.
    for (const e of await tx.listFailedTerminal()) {
      if (e.op === 'penalty.add' && matches(e, assignmentId, clientIdempotencyKey))
        await tx.updateOutbox(e.id, { status: 'acked' });
    }
    const payload: PenaltyRemovePayload = { roundAssignmentId: assignmentId, clientIdempotencyKey };
    await enqueue('penalty.remove', payload, tx);
  });
}

/**
 * What the score screen shows for one assignment: server penalties, plus
 * queued adds not yet on the server, minus queued removes. `entries` are the
 * unresolved outbox entries (pending + inflight + failed_terminal, any
 * assignment — filtered here). `values` (liveAssignments `penaltyValues`)
 * fills in the points of queued adds.
 */
export function mergePenaltyView(
  assignmentId: string,
  server: readonly ServerPenaltyRow[],
  entries: readonly OutboxEntry[],
  values?: Partial<Record<PenaltyCode, number>>,
): PenaltyViewRow[] {
  const rows = new Map<string, PenaltyViewRow>();
  for (const p of server) {
    const key = p.clientIdempotencyKey ?? p.id;
    rows.set(key, {
      key,
      id: p.id,
      clientIdempotencyKey: p.clientIdempotencyKey,
      code: p.code,
      points: p.points,
      note: p.note ?? null,
      status: 'synced',
    });
  }
  const ordered = entries
    .filter(
      (e) =>
        (e.op === 'penalty.add' || e.op === 'penalty.remove') &&
        e.status !== 'acked' &&
        assignmentOf(e) === assignmentId,
    )
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  for (const e of ordered) {
    const failed = e.status === 'failed_terminal';
    const extra = failed && e.lastError ? { lastError: e.lastError } : {};
    if (e.op === 'penalty.add') {
      const p = e.payload as PenaltyAddPayload;
      if (rows.has(p.clientIdempotencyKey)) continue; // landed already
      rows.set(p.clientIdempotencyKey, {
        key: p.clientIdempotencyKey,
        id: null,
        clientIdempotencyKey: p.clientIdempotencyKey,
        code: p.code,
        points: values?.[p.code] ?? null,
        note: p.note ?? null,
        status: failed ? 'failed' : 'pending',
        ...extra,
      });
    } else {
      const key = (e.payload as PenaltyRemovePayload).clientIdempotencyKey;
      const row = rows.get(key);
      if (!row) continue;
      // A rejected remove leaves the penalty in place — show it as failed.
      if (failed) rows.set(key, { ...row, status: 'failed', ...extra });
      else rows.delete(key);
    }
  }
  return [...rows.values()];
}

/** Sum of the shown penalty points (queued adds without known points count 0). */
export function penaltyTotal(rows: readonly PenaltyViewRow[]): number {
  return rows.reduce((n, r) => n + (r.points ?? 0), 0);
}

/**
 * Ack penalty outbox entries that were marked failed_terminal although their
 * write reached the server (e.g. a timeout after commit followed by a 4xx on
 * retry). Without this a landed entry would keep blocking the assignment's
 * queue. Call it with fresh server truth (penalties.listByAssignment).
 * Returns the number of entries acked.
 */
export async function reconcilePenalties(
  assignmentId: string,
  server: readonly ServerPenaltyRow[],
  store: SyncStore = getDexieStore(),
): Promise<number> {
  const keys = new Set(
    server.map((p) => p.clientIdempotencyKey).filter((k): k is string => k != null),
  );
  return store.transaction(async (tx) => {
    let acked = 0;
    const failed = (await tx.listFailedTerminal()).sort((a, b) => a.createdAt - b.createdAt);
    for (const e of failed) {
      if (assignmentOf(e) !== assignmentId) continue;
      const key = (e.payload as { clientIdempotencyKey?: string }).clientIdempotencyKey;
      if (!key) continue;
      const landed =
        (e.op === 'penalty.add' && keys.has(key)) ||
        (e.op === 'penalty.remove' && e.attempts > 0 && !keys.has(key));
      if (landed) {
        await tx.updateOutbox(e.id, { status: 'acked' });
        acked++;
      }
    }
    return acked;
  });
}

/**
 * A penalty add/remove that reached the server while the roster the screen
 * shows (sessions.liveAssignments) still predates it — e.g. the roster
 * refetch after the upload failed because the device went offline again.
 * Acked entries leave the unresolved outbox, so the native app keeps these in
 * its own persisted list and applies them on top of the roster until the
 * roster agrees (`pruneLandedPenalties`). At most one entry per idempotency
 * key: the latest landed op wins.
 */
export interface LandedPenalty {
  /** Outbox entry id + createdAt (keeps the add → remove order in the merge). */
  id: string;
  createdAt: number;
  op: 'penalty.add' | 'penalty.remove';
  payload: PenaltyAddPayload | PenaltyRemovePayload;
  /** When it was recorded (age cap). */
  landedAt: number;
}

/** Record a landed penalty entry (other ops are ignored); replaces an older entry of the same key. */
export function addLandedPenalty(
  list: readonly LandedPenalty[],
  entry: OutboxEntry,
  now: number,
): LandedPenalty[] {
  if (entry.op !== 'penalty.add' && entry.op !== 'penalty.remove') return [...list];
  const payload = entry.payload as PenaltyAddPayload | PenaltyRemovePayload | null;
  const key = payload?.clientIdempotencyKey;
  if (!payload || !key) return [...list];
  return [
    ...list.filter((l) => l.payload.clientIdempotencyKey !== key),
    { id: entry.id, createdAt: entry.createdAt, op: entry.op, payload, landedAt: now },
  ];
}

/**
 * Drop landed entries the server roster already reflects (an add whose key
 * is in `server`, a remove whose key is not) and entries older than
 * `maxAgeMs`. `server` = the roster's penalties of the same assignment, null
 * while no roster row is loaded (then only the age cap applies).
 */
export function pruneLandedPenalties(
  list: readonly LandedPenalty[],
  server: readonly ServerPenaltyRow[] | null,
  now: number,
  maxAgeMs: number,
): LandedPenalty[] {
  const keys = server
    ? new Set(server.map((p) => p.clientIdempotencyKey).filter((k): k is string => k != null))
    : null;
  return list.filter((l) => {
    if (now - l.landedAt > maxAgeMs) return false;
    if (!keys) return true;
    const has = keys.has(l.payload.clientIdempotencyKey);
    return l.op === 'penalty.add' ? !has : has;
  });
}

/** Landed entries as outbox entries for `mergePenaltyView` (shown like queued ones). */
export function landedPenaltyEntries(list: readonly LandedPenalty[]): OutboxEntry[] {
  return list.map((l) => ({
    id: l.id,
    op: l.op,
    payload: l.payload,
    createdAt: l.createdAt,
    attempts: 1,
    status: 'pending' as const,
  }));
}

function matches(e: OutboxEntry, assignmentId: string, key: string): boolean {
  const p = e.payload as { roundAssignmentId?: string; clientIdempotencyKey?: string };
  return p.roundAssignmentId === assignmentId && p.clientIdempotencyKey === key;
}
